import React from 'react';
import { shape, string, arrayOf } from 'prop-types';
import styled from 'styled-components';
import { getImage } from 'gatsby-plugin-image';
import BlogCard from 'TMComponents/BlogCard';
import { Button } from 'TMComponents/Button';
import { H2, BodyReg } from 'TMComponents/Typography';
import { spacing } from 'TMDesignSystem/spacing';
import { breakpoints } from 'TMConstants';

const Section = styled.section`
    margin-top: ${spacing.xxxLarge};
    margin-bottom: 0;
`;

const Title = styled(H2)`
    margin-bottom: ${spacing.medium};

    .orange-dot {
        color: ${({ theme }) => theme.colorSystem.secondary[3]};
    }
`;

const Description = styled(BodyReg)`
    margin-bottom: ${spacing.large};
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${spacing.mediumLarge};
    margin-bottom: ${spacing.xLarge};

    @media (min-width: ${breakpoints.mobileTablet}px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: ${breakpoints.tabletDesktop}px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
`;

const RelatedArticles = ({
    title,
    description,
    articles,
    buttonText,
    buttonLink
}) => {
    if (!articles || articles.length === 0) return null;

    const renderTitle = () => {
        if (!title) return null;
        const lastChar = title.slice(-1);
        const titleWithoutLast = title.slice(0, -1);

        return (
            <Title variant="xs">
                {titleWithoutLast}
                <span className="orange-dot">{lastChar}</span>
            </Title>
        );
    };

    return (
        <Section>
            {renderTitle()}
            {description && <Description>{description}</Description>}
            <CardsGrid>
                {articles.map((article) => (
                    <BlogCard
                        key={article.id}
                        title={article.title}
                        slug={article.slug}
                        image={getImage(article.featuredImage)}
                        excerpt={article.summary}
                        tags={
                            article.categories?.map((cat) => cat.name) ||
                            []
                        }
                        variant="default"
                    />
                ))}
            </CardsGrid>
            {buttonText && buttonLink && (
                <ButtonWrapper>
                    <Button link={buttonLink}>{buttonText}</Button>
                </ButtonWrapper>
            )}
        </Section>
    );
};

RelatedArticles.propTypes = {
    title: string,
    description: string,
    buttonText: string,
    buttonLink: string,
    articles: arrayOf(
        shape({
            id: string,
            title: string,
            slug: string,
            summary: string,
            featuredImage: shape({}),
            categories: arrayOf(shape({ name: string }))
        })
    )
};

export default RelatedArticles;
